import React, { useRef, useState } from "react";
import DropzonePrimitive, { DropzoneOptions } from "react-dropzone";
import { useController } from "react-hook-form";
import { cn, toast } from "@coninsa/core";

import type {
  FieldValues,
  FieldPath,
  UseControllerProps,
} from "react-hook-form";

import "./style.css";
import pictureURL from "../assets/picture.svg";

// Value in KBytes
const S3_MAX_FILE_SIZE = 200;

type T_props<
  T_fieldValues extends FieldValues,
  T_name extends FieldPath<T_fieldValues>
> = UseControllerProps<T_fieldValues, T_name> & {
  max?: number;
  asImage?: boolean;
} & DropzoneOptions;

export function UploadImagesS3<
  T_fieldValues extends FieldValues,
  T_name extends FieldPath<T_fieldValues>
>({
  name,
  control,
  // @ts-ignore
  defaultValue = [],
  max = 10,
  asImage = true,
  ...props
}: T_props<T_fieldValues, T_name>) {
  const { field } = useController({ name, control, defaultValue });
  const [loading, setLoading] = useState(false);

  const handleChange = async (files: Array<File>) => {
    if (files.length > 0) {
      const filePromises = files.map(async (file) => {
        // Bytes to KBytes
        const fileSize = file.size / 1000;

        if (fileSize < S3_MAX_FILE_SIZE) {
          const data = new FormData();
          data.append("photo", file);

          const response = await fetch(
            "https://admindrupal.coninsa.co/api/nuwwe/properties/{code}/s3-image",
            {
              method: "POST",
              body: data,
            }
          );

          return await response.json();
        }

        return Promise.reject(
          `La imagen ${file.name} excede el tamaño máximo permitido (500 KB).`
        );
      });

      const results = await Promise.allSettled(filePromises);
      const externalFiles = results
        .filter((result) => result.status === "fulfilled")
        .map((item) => item.value);

      results
        .filter((result) => result.status === "rejected")
        .forEach((item) => {
          toast.error({ message: item.reason });
        });

      setLoading(false);

      if (field.value) {
        field.onChange([...field.value, ...externalFiles]);
      } else {
        field.onChange(externalFiles);
      }
    }
  };

  const onRemove = (excludeIndex) => () => {
    const filteredValues = field.value.filter(
      (_, index) => index !== excludeIndex
    );

    field.onChange(filteredValues);
  };

  const inputId = `${name}-id`;
  const hasImages = field.value?.length > 0;

  return (
    <div
      className={`form-control is-dropzone ${hasImages ? "has-images" : ""}`}
    >
      <div className="dropzone__items">
        {field?.value?.length < max && (
          <DropzonePrimitive
            onDrop={handleChange}
            disabled={props.disabled || loading}
            {...props}
          >
            {({ getRootProps, getInputProps }) => (
              <section className={cn(!hasImages && "flex-1")}>
                <div {...getRootProps({ className: "form-dropzone" })}>
                  <input {...getInputProps()} />

                  <label>
                    <img src={pictureURL} />

                    {loading && (
                      <p
                        className={cn(
                          "font-bold text-center",
                          hasImages && "text-xs"
                        )}
                      >
                        Cargando ...
                      </p>
                    )}

                    {!loading && (
                      <p
                        className={cn(
                          "font-bold text-center",
                          hasImages && "text-xs"
                        )}
                      >
                        Agrega o arrastra tus fotografías en este recuadro
                        {!hasImages && (
                          <>
                            <br />
                            <span className="font-light opacity-60">
                              Formatos soportados JPG, JPEG2000, PNG
                            </span>
                          </>
                        )}
                      </p>
                    )}
                  </label>
                </div>
              </section>
            )}
          </DropzonePrimitive>
        )}

        {hasImages &&
          field.value.map((item, index) => (
            <div key={index} className="dropzone__item">
              {asImage && <img src={item.url} title={item.name} />}
              {!asImage && (
                <a href={item.url} target="_blank" rel="noreferrer">
                  {item.name}
                </a>
              )}

              <button
                className="dropzone__item__remove"
                onClick={onRemove(index)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                  <path
                    fill="currentColor"
                    d="M170.5 51.6L151.5 80h145l-19-28.4c-1.5-2.2-4-3.6-6.7-3.6H177.1c-2.7 0-5.2 1.3-6.7 3.6zm147-26.6L354.2 80H368h48 8c13.3 0 24 10.7 24 24s-10.7 24-24 24h-8V432c0 44.2-35.8 80-80 80H112c-44.2 0-80-35.8-80-80V128H24c-13.3 0-24-10.7-24-24S10.7 80 24 80h8H80 93.8l36.7-55.1C140.9 9.4 158.4 0 177.1 0h93.7c18.7 0 36.2 9.4 46.6 24.9zM80 128V432c0 17.7 14.3 32 32 32H336c17.7 0 32-14.3 32-32V128H80zm80 64V400c0 8.8-7.2 16-16 16s-16-7.2-16-16V192c0-8.8 7.2-16 16-16s16 7.2 16 16zm80 0V400c0 8.8-7.2 16-16 16s-16-7.2-16-16V192c0-8.8 7.2-16 16-16s16 7.2 16 16zm80 0V400c0 8.8-7.2 16-16 16s-16-7.2-16-16V192c0-8.8 7.2-16 16-16s16 7.2 16 16z"
                  />
                </svg>
              </button>
            </div>
          ))}
      </div>

      <p className="help-text text-[#00706A]">
        Has cargado {field.value.length} de {max} fotografías recomendadas
      </p>
    </div>
  );
}
